import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import Module from '🔗/worklets/rlottie/rlottie-wasm.js';
import pako from 'pako';

import { TGS_STRATEGIES } from '🔗/constants';

// Engine
    let wasmEngine = null;

    const getWasmEngine = async () => {
        if (wasmEngine) {
            return wasmEngine;
        }

        wasmEngine = typeof Module === 'function' ? await Module() : await Module;
        return wasmEngine;
    };

    const dpr = Math.min(window.devicePixelRatio || 1, 2);


class StickerResource {
    constructor(url, size=200) {
        this.url = url;
        this.handle = null;
        this.lastFrame = -1; // Для кэширования

        this.size = size * dpr;

        this.tempCanvas = document.createElement('canvas');
        this.tempCanvas.width = this.size;
        this.tempCanvas.height = this.size;
        this.tempCtx = this.tempCanvas.getContext('2d');
        this.tempImageData = this.tempCtx.createImageData(this.size, this.size);

        this.globalFrame = 0;
        // Внутри класса StickerResource
        this.refCount = 0;
    }

    acquire() {
        this.refCount++;
    }

    release() {
        this.refCount--;
    }

    advance(dt = 1) {
        this.globalFrame += dt;
    }

    async load() {
        const engine = await getWasmEngine();
        const res = await fetch(this.url);
        const buf = await res.arrayBuffer();
        const json = pako.inflate(new Uint8Array(buf), { to: 'string' });

        this.handle = new engine.RlottieWasm();
        this.handle.load(json);
        this.totalFrames = this.handle.frames();
    }

    updateSize(newSize) {
        this.size = newSize * dpr;
        this.tempCanvas.width = this.size;
        this.tempCanvas.height = this.size;
        this.tempImageData = this.tempCtx.createImageData(this.size, this.size);
        this.tempCtx = this.tempCanvas.getContext('2d');
        this.lastFrame = -1; // Сбрасываем кэш кадра, чтобы перерендерить в новом размере
    }

    render() {
        if (!this.handle) {
            return null;
        }

        // const currentFrame = this.globalFrame % this.totalFrames;
        const currentFrame = Math.floor(this.globalFrame) % this.totalFrames;

        if (this.lastFrame === currentFrame) {
            return this.tempCanvas;
        }


        const buffer = this.handle.render(currentFrame, this.size, this.size);
        this.tempImageData.data.set(buffer);
        this.tempCtx.putImageData(this.tempImageData, 0, 0);
        
        this.lastFrame = currentFrame;
        return this.tempCanvas;
    }
}

class Particle {
    constructor(resource, x, y, speed, type) {
        this.res = resource;
        this.x = x;
        this.y = y;
        this.frame = 0;
        this.opacity = 1;
        this.scale = 0.5 + Math.random() * 0.5;
        this.rotation = 0;
        this.life = 1.0; 
        
        // Инициализируем свойства по умолчанию, чтобы update не падал
        this.vx = 0;
        this.vy = 0;
        this.gravity = 0;
        this.rotationSpeed = 0;
        this.useOpacity = false;
        this.useOscillation = false;

        // Применяем выбранную стратегию
        const strategy = TGS_STRATEGIES[type] || TGS_STRATEGIES.burst;
        strategy(this, speed);
    }

    update(w, h, dt = 1) {
        if (this.isGlass) {
            // Логика для эффекта стекла
            if (this.status === 'flying') {
                this.x += this.vx * dt;
                this.y += this.vy * dt;
                
                // Корректный lerp (плавное приближение) для разной герцовки
                const lerpFactor = 1 - Math.pow(1 - 0.1, dt);
                this.scale += (this.targetScale - this.scale) * lerpFactor;
                this.opacity += 0.1 * dt;

                // Проверка достижения цели
                if (Math.abs(this.x - this.targetX) < 2) {
                    this.status = 'hit';
                }
            } 
            else if (this.status === 'hit') {
                this.hitDuration -= dt;
                this.x += (Math.random() - 0.5) * 2 * dt; // Дрожание

                if (this.hitDuration <= 0) {
                    this.status = 'sliding';
                }
            } 
            else if (this.status === 'sliding') {
                this.y += this.slideSpeed * dt;
                this.opacity -= 0.01 * dt;
            }
        } else {
            // Стандартная физика (burst, rain, fountain, float)
            if (this.useOscillation) {
                this.vx = Math.sin(this.frame * 0.1) * 1.5;
            }

            this.x += this.vx * dt;
            this.y += this.vy * dt;
            this.vy += this.gravity * dt;
            this.rotation += this.rotationSpeed * dt;

            if (this.useOpacity) {
                this.life -= 0.01 * dt;
                this.opacity = this.life;
            }
        }

        this.frame += dt;
        const m = (this.res.size / dpr) * this.scale;

        // Условие удаления: прозрачность или выход за границы
        return this.opacity > 0 && this.y < h + m && this.x > -m && this.x < w + m;
    }


    draw(mainCtx) {
        const img = this.res.render(); 
        if (!img) {
            return;
        }

        const drawSize = (this.res.size / dpr) * this.scale;

        mainCtx.save();
        mainCtx.globalAlpha = Math.max(0, Math.min(1, this.opacity));
        mainCtx.translate(this.x, this.y);
        mainCtx.rotate(this.rotation);
        mainCtx.drawImage(img, -drawSize/2, -drawSize/2, drawSize, drawSize);
        mainCtx.restore();
    }
}

const useTgsStore = defineStore('tgs', {
    state: () => ({
        dpr,
        resourcesCache: new Map(),
        maxCacheSize: 15,
        explode: async () => {},
        loadingPromises: new Map(),
        viewers: new Set(),
        animationFrame: null
    }),
    actions: {
        registerViewer(viewer) {
            this.viewers.add(viewer);
            if (!this.animationFrame) {
                this.startGlobalLoop();
            }
        },
        unregisterViewer(viewer) {
            this.viewers.delete(viewer);
            if (this.viewers.size === 0 && this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
        },
        startGlobalLoop() {
            let lastTime = performance.now();

            const loop = (currentTime) => {
                // Вычисляем дельту. Если 60 FPS, то delta будет ~1.0
                // Если 120 FPS, то delta будет ~0.5
                const deltaTime = Math.min((currentTime - lastTime) / (1000 / 60), 2.0);
                lastTime = currentTime;

                // 1. Сначала один раз обновляем кадры всех ресурсов в кэше
                const activeResources = new Set();

                this.viewers.forEach(item => {
                    if (item.resource && item.isVisible) {
                        activeResources.add(item.resource);
                    }
                });
                activeResources.forEach(res => res.advance(deltaTime));

                // 2. Затем просим каждый вьювер перерисовать свой канвас
                this.viewers.forEach(item => item.draw());

                this.animationFrame = requestAnimationFrame(loop);
            };

            this.animationFrame = requestAnimationFrame(loop);
        },
        setExplode(fn) {
            this.explode = fn;
        },
        createSticker(...params) {
            return markRaw(new StickerResource(...params));
        },
        async createCacheSticker(url, size) {
            // 1. Если уже загружено — отдаем сразу
            let s = this.resourcesCache.get(url);
            if (s) {
                if (size * this.dpr > s.size) {
                    s.updateSize(size);
                }

                this.resourcesCache.delete(url);
                this.resourcesCache.set(url, s);
                return s;
            }

            // 2. Если сейчас КТО-ТО ДРУГОЙ УЖЕ ГРУЗИТ этот URL — ждем тот же самый промис
            if (this.loadingPromises.has(url)) {
                return this.loadingPromises.get(url);
            }

            // 3. Если мы первые — создаем промис загрузки
            const loadPromise = (async () => {
                try {
                    const sticker = this.createSticker(url, size);
                    await sticker.load();
                    this.resourcesCache.set(url, sticker);
                    return sticker;
                } finally {
                    // Удаляем из списка ожидающих после завершения (успешного или нет)
                    this.loadingPromises.delete(url);
                }
            })();

            this.loadingPromises.set(url, loadPromise);
            return loadPromise;
        },
        clearCache() {
            this.resourcesCache.forEach((sticker) => {
                // Здесь удаляем принудительно, игнорируя refCount
                if (sticker.handle?.destroy) {
                    sticker.handle.destroy();
                }
            });

            this.resourcesCache.clear();
        },
        disposeSticker(url) {
            const sticker = this.resourcesCache.get(url);
            if (!sticker) {
                return;
            }

            if (sticker.refCount <= 0) {
                if (sticker.handle?.destroy) {
                    sticker.handle.destroy();
                }

                this.resourcesCache.delete(url);
                return true;
            }
            return false;
        },

        createParticle(...params) {
            return markRaw(new Particle(...params));
        },

        async prepareResources(urls, size) {
            const newUrls = urls.filter(url => !this.resourcesCache.has(url));
            const uniqueNewCount = new Set(newUrls).size;

            if (uniqueNewCount > 0 && (this.resourcesCache.size + uniqueNewCount > this.maxCacheSize)) {
                const overflow = (this.resourcesCache.size + uniqueNewCount) - this.maxCacheSize;
                const allKeys = Array.from(this.resourcesCache.keys());
                let removed = 0;
                for (const url of allKeys) {
                    if (removed >= overflow) break;
                    if (this.disposeSticker(url)) removed++;
                }
            }

            return Promise.all(urls.map(url => this.createCacheSticker(url, size)));
        },
        spawnParticle(res, x, y, speed, type) {
            res.acquire();
            const p = this.createParticle(res, x, y, speed, type);
            const originalUpdate = p.update;
            
            // Инкапсулируем логику освобождения внутри метода обновления
            p.update = (w, h, dt) => {
                const alive = originalUpdate.call(p, w, h, dt);
                if (!alive) {
                    res.release();
                }

                return alive;
            };

            return p;
        },
        async getEngine() {
            return await getWasmEngine();
        },
        async init() {
            await this.getEngine();
        }
    }
});

export default useTgsStore;
